import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaExclamationTriangle, FaUndoAlt, FaClock, FaCheckCircle, FaTimesCircle,
    FaSearch, FaFilter, FaChevronDown, FaChevronRight, FaEye, FaBoxOpen,
    FaTruck, FaExchangeAlt, FaChartLine, FaCog
} from 'react-icons/fa';
import {
    HiOutlineExclamationCircle, HiOutlineRefresh, HiOutlineClock,
    HiOutlineCheck, HiOutlineX
} from 'react-icons/hi';
import { MdDashboard, MdWarning, MdAssignment } from 'react-icons/md';
import { toast } from 'react-toastify';
import { getPostOrderDashboard, getPostOrderAnalytics } from '../../services/ClientService';

const PostOrderCommandCenter = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('all');
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [summary, setSummary] = useState({ exceptions: 0, returns: 0, delayed: 0 });
    const [search, setSearch] = useState('');
    const [analytics, setAnalytics] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const searchTimeoutRef = useRef(null);
    const PAGE_SIZE = 12;

    const fetchDashboard = useCallback(async (currentPage = 1, currentFilter = activeTab) => {
        try {
            setLoading(true);
            const response = await getPostOrderDashboard({
                page: currentPage,
                limit: PAGE_SIZE,
                filter: currentFilter === 'all' ? undefined : currentFilter
            });
            if (response?.success) {
                if (currentPage === 1) {
                    setOrders(response.data || []);
                } else {
                    setOrders(prev => [...prev, ...(response.data || [])]);
                }
                setTotalCount(response.totalCount || 0);
                setSummary(response.summary || { exceptions: 0, returns: 0, delayed: 0 });
            }
        } catch (error) {
            toast.error('Failed to load post-order data');
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    const fetchAnalytics = useCallback(async () => {
        try {
            const response = await getPostOrderAnalytics({});
            if (response?.success) {
                setAnalytics(response.data);
            }
        } catch (error) {
            console.error('Analytics fetch failed:', error);
        }
    }, []);

    useEffect(() => {
        fetchDashboard(1, activeTab);
    }, [activeTab, fetchDashboard]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);
    };

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 100 && !loading && orders.length < totalCount) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchDashboard(nextPage, activeTab);
        }
    }, [loading, orders.length, totalCount, page, activeTab, fetchDashboard]);

    const getStatusBadge = (status) => {
        const statusStyles = {
            'Received': 'bg-blue-100 text-blue-700',
            'Confirmed': 'bg-indigo-100 text-indigo-700',
            'Shipped': 'bg-purple-100 text-purple-700',
            'Out For Delivery': 'bg-amber-100 text-amber-700',
            'Delivered': 'bg-green-100 text-green-700',
            'Cancelled': 'bg-red-100 text-red-700',
            'Returned': 'bg-orange-100 text-orange-700'
        };
        return statusStyles[status] || 'bg-gray-100 text-gray-700';
    };

    const tabs = [
        { key: 'all', label: 'All Issues', icon: <MdDashboard />, count: summary.exceptions + summary.returns + summary.delayed },
        { key: 'exceptions', label: 'Exceptions', icon: <FaExclamationTriangle />, count: summary.exceptions },
        { key: 'returns', label: 'Returns', icon: <FaUndoAlt />, count: summary.returns },
        { key: 'delayed', label: 'Delayed', icon: <FaClock />, count: summary.delayed },
        { key: 'delivered', label: 'Delivered', icon: <FaCheckCircle />, count: null }
    ];

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <MdDashboard className="text-indigo-600" />
                            Post-Order Command Center
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Unified view of exceptions, returns, and post-delivery issues
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowAnalytics(!showAnalytics)}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-all text-sm font-medium"
                        >
                            <FaChartLine />
                            {showAnalytics ? 'Hide Analytics' : 'View Analytics'}
                        </button>
                        <button
                            onClick={() => navigate('/post-order-settings')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all text-sm font-medium"
                        >
                            <FaCog />
                            Settings
                        </button>
                    </div>
                </div>
            </div>

            {/* Analytics Panel */}
            <AnimatePresence>
                {showAnalytics && analytics && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        <div className="bg-white border-b border-gray-200 px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FaUndoAlt className="text-orange-500" />
                                        <span className="text-sm font-medium text-orange-700">Total Returns</span>
                                    </div>
                                    <p className="text-2xl font-bold text-orange-900">{analytics.summary?.totalReturns || 0}</p>
                                </div>
                                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FaTimesCircle className="text-red-500" />
                                        <span className="text-sm font-medium text-red-700">Total Refunded</span>
                                    </div>
                                    <p className="text-2xl font-bold text-red-900">₹{(analytics.summary?.totalRefunded || 0).toLocaleString()}</p>
                                </div>
                                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FaExclamationTriangle className="text-yellow-500" />
                                        <span className="text-sm font-medium text-yellow-700">Total Exceptions</span>
                                    </div>
                                    <p className="text-2xl font-bold text-yellow-900">{analytics.summary?.totalExceptions || 0}</p>
                                </div>
                                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <MdWarning className="text-amber-500" />
                                        <span className="text-sm font-medium text-amber-700">Unresolved</span>
                                    </div>
                                    <p className="text-2xl font-bold text-amber-900">{analytics.summary?.unresolvedExceptions || 0}</p>
                                </div>
                            </div>

                            {/* QC Decisions breakdown */}
                            {analytics.qcDecisions && analytics.qcDecisions.length > 0 && (
                                <div className="mt-4 flex items-center gap-4">
                                    <span className="text-sm font-medium text-gray-600">QC Decisions:</span>
                                    {analytics.qcDecisions.map((qc) => (
                                        <span key={qc._id} className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                            {qc._id}: {qc.count}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tabs */}
            <div className="bg-white border-b border-gray-200 px-6">
                <div className="flex gap-1 overflow-x-auto scrollbar-thin">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => handleTabChange(tab.key)}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                                activeTab === tab.key
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                            {tab.count !== null && tab.count > 0 && (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    activeTab === tab.key ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Orders List */}
            <div className="flex-1 min-h-0 px-6 py-4 overflow-y-auto" onScroll={handleScroll}>
                {loading && orders.length === 0 ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100 animate-pulse">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                                    </div>
                                    <div className="h-8 bg-gray-200 rounded-lg w-24"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-16">
                        <FaCheckCircle className="mx-auto text-green-400 text-5xl mb-4" />
                        <h3 className="text-lg font-semibold text-gray-700">All Clear!</h3>
                        <p className="text-gray-500 mt-1">No post-order issues found for this filter.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {orders.map((order) => (
                            <motion.div
                                key={order._id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-white rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all cursor-pointer"
                                onClick={() => navigate(`/orderOverview/${order._id}`)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                            order.hasExceptions ? 'bg-red-100' :
                                            order.hasReturnRequest ? 'bg-orange-100' :
                                            'bg-green-100'
                                        }`}>
                                            {order.hasExceptions ? <FaExclamationTriangle className="text-red-500 text-lg" /> :
                                             order.hasReturnRequest ? <FaUndoAlt className="text-orange-500 text-lg" /> :
                                             <FaBoxOpen className="text-green-500 text-lg" />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-gray-900">{order.saleId}</h3>
                                                <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${getStatusBadge(order.status)}`}>
                                                    {order.status}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500 mt-0.5">
                                                {order.delivery?.addressDetails?.name || order.customerName || 'Customer'} • {' '}
                                                {new Date(order.orderPlacedOn || order.createdAt).toLocaleDateString('en-IN', {
                                                    day: 'numeric', month: 'short', year: 'numeric'
                                                })}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {(order.productsInfo || []).length} items • ₹{(order.financial?.finalAmount || order.grandTotal || order.finalAmount || 0).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        {/* Exception/Return badges */}
                                        <div className="flex flex-wrap gap-1 justify-end">
                                            {order.hasExceptions && (
                                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-600 border border-red-200">
                                                    <FaExclamationTriangle className="text-[10px]" /> Exception
                                                </span>
                                            )}
                                            {order.hasReturnRequest && (
                                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-orange-50 text-orange-600 border border-orange-200">
                                                    <FaUndoAlt className="text-[10px]" /> Return
                                                </span>
                                            )}
                                            {order.hasPartialReturn && (
                                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-yellow-50 text-yellow-600 border border-yellow-200">
                                                    Partial Return
                                                </span>
                                            )}
                                        </div>
                                        <FaChevronRight className="text-gray-400 text-sm" />
                                    </div>
                                </div>

                                {/* Exception list inline preview */}
                                {order.exceptions && order.exceptions.filter(e => !e.isResolved).length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="flex flex-wrap gap-2">
                                            {order.exceptions.filter(e => !e.isResolved).map((exc, idx) => (
                                                <span key={idx} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-red-50 text-red-700">
                                                    <MdWarning className="text-xs" />
                                                    {exc.type?.replace(/_/g, ' ')} {exc.severity === 'critical' && '🔴'}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        ))}

                        {loading && (
                            <div className="text-center py-4">
                                <div className="inline-block w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PostOrderCommandCenter;
